import { Component, Inject, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Author, AuthorWithRelations } from "src/app/api/models";
import { TinyService } from "src/app/shared/services/tiny.service";

export interface AuthorCreateUpdateDialogData {
  context: "editor" | "inventory" | "item";
  author?: AuthorWithRelations;
}

export interface AuthorCreateUpdateDialogResult {
  action: "create" | "update" | "delete";
  author?: Author;
}

@Component({
  selector: "app-author-create-update-dialog",
  templateUrl: "./author-create-update-dialog.component.html",
  styleUrls: ["./author-create-update-dialog.component.scss"],
  standalone: false,
})
export class AuthorCreateUpdateDialogComponent implements OnInit {
  form: UntypedFormGroup;
  mode: "create" | "update" = "create";
  context: "editor" | "inventory" | "item";
  author: AuthorWithRelations;

  constructor(
    @Inject(MAT_DIALOG_DATA) public defaults: AuthorCreateUpdateDialogData,
    private dialogRef: MatDialogRef<AuthorCreateUpdateDialogComponent>,
    private fb: UntypedFormBuilder,
    public tinyService: TinyService
  ) {
    this.form = this.fb.group({
      firstname: [""],
      lastname: [""],
      nickname: ["", [Validators.required]],
      birthday: [""],
      death: [""],
      descriptionCS: [""],
      descriptionEN: [""],
      canvasId: [null],
      curated: [false],
    });
  }

  async ngOnInit(): Promise<void> {
    this.context = this.defaults.context;
    this.author = this.defaults.author ?? ({} as AuthorWithRelations);
    if (this.defaults.author) {
      this.mode = "update";
      this.form.patchValue(this.author);
    }
    if (!this.canEdit()) {
      this.form.disable({ emitEvent: false });
    }
    if (this.author.canvasId) {
      this.form.get("canvasId")?.disable({ emitEvent: false });
    }
  }

  /** Default submit function */
  save() {
    let author: Author | undefined;
    switch (this.mode) {
      case "create":
        author = this.prepareNewAuthor();
        break;
      case "update":
        author = this.prepareUpdateAuthor();
        break;
      default:
        break;
    }
    const result: AuthorCreateUpdateDialogResult = {
      action: this.mode,
      author,
    };
    this.dialogRef.close(result);
  }

  /* ---------------------------------------------------------
  SPECIFIC METHODS
  --------------------------------------------------------- */
  prepareNewAuthor(): Author {
    const author: Author = this.form.value;
    if (author.canvasId) {
      if (author.canvasId === 0) {
        delete author.canvasId;
      }
    } else if (author.canvasId === null) {
      delete author.canvasId;
    }
    return author;
  }

  prepareUpdateAuthor(): Author {
    const author: Author = this.form.value;
    if (author.canvasId) {
      if (author.canvasId === 0) {
        delete author.canvasId;
      }
    } else if (author.canvasId === null) {
      delete author.canvasId;
    }
    author.id = this.author.id;
    return author;
  }

  /* ---------------------------------------------------------
  DIALOG CONTENT METHODS
  --------------------------------------------------------- */
  isCreateMode() {
    return this.mode === "create";
  }

  isUpdateMode() {
    return this.mode === "update";
  }

  isEditorContext() {
    return this.context === "editor";
  }

  isInventoryContext() {
    return this.context === "inventory";
  }

  canEdit() {
    const isLocalAuthor = this.defaults.author?.inventoryId;
    return (
      this.isEditorContext() ||
      (this.isUpdateMode() && isLocalAuthor && this.isInventoryContext()) ||
      this.isCreateMode()
    );
  }
}
